<template>
  <div class="category-editor">
    <div class="category-editor__header">
      <v-btn icon class="category-editor__back" aria-label="Back" @click="goBack">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <div class="category-editor__title">
        <span>Categories</span>
        <h2>{{ formTitle }}</h2>
      </div>
    </div>

    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
      class="category-editor__form"
      @submit.prevent="handleSubmit"
    >
      <v-expansion-panels v-model="openPanels" multiple class="category-sections" flat>
        <v-expansion-panel class="category-section">
          <v-expansion-panel-header>
            <div class="category-section__header">
              <span>Step 1</span>
              <strong>Details</strong>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="form-grid form-grid--two">
              <v-text-field
                v-model="name"
                label="Name"
                :rules="nameRules"
                required
                outlined
                dense
                hide-details="auto"
              />

              <v-select
                :items="categories"
                item-text="name"
                v-model="parentCategory"
                item-value="id"
                return-object
                label="Parent Category"
                outlined
                dense
                hide-details="auto"
              ></v-select>

              <v-textarea
                v-model="description"
                label="Description"
                outlined
                dense
                auto-grow
                rows="2"
                hide-details="auto"
              />

              <v-textarea
                v-model="popupNotes"
                label="Popup Notes"
                outlined
                dense
                auto-grow
                rows="2"
                hide-details="auto"
              />
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="category-section">
          <v-expansion-panel-header>
            <div class="category-section__header">
              <span>Step 2</span>
              <strong>Options</strong>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="option-grid">
              <label class="option-card">
                <v-checkbox v-model="ageCheck" label="Age Check" hide-details></v-checkbox>
              </label>
              <label class="option-card">
                <v-checkbox v-model="isExcludedFromDiscount" label="Excluded From Discount" hide-details></v-checkbox>
              </label>
              <label class="option-card">
                <v-checkbox v-model="eposOnly" label="EPOS Only" hide-details></v-checkbox>
              </label>
              <label class="option-card">
                <v-checkbox v-model="hidden" label="Hidden From UI (EPOS)" hide-details></v-checkbox>
              </label>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel class="category-section">
          <v-expansion-panel-header>
            <div class="category-section__header">
              <span>Step 3</span>
              <strong>Image</strong>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="image-uploader">
              <v-file-input
                label="Image"
                truncate-length="15"
                show-size
                accept="image/*"
                capture="environment"
                prepend-icon=""
                prepend-inner-icon="mdi-camera"
                v-model="image"
                outlined
                dense
                hide-details="auto"
                @change="upload(image)"
              ></v-file-input>

              <div v-if="imageBase64 != null" class="image-preview">
                <img :src="imageBase64" alt="Category preview" />
                <v-btn type="button" text color="error" @click="removeImage">
                  <v-icon left small>mdi-delete</v-icon>
                  Remove image
                </v-btn>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="category-editor__save-spacer"></div>

      <div class="category-save-bar">
        <div class="category-save-bar__actions">
          <v-btn type="button" text @click="goBack">Cancel</v-btn>
          <v-btn type="submit" :disabled="!valid || isSaving" :loading="isSaving" color="primary" depressed>
            Save
          </v-btn>
        </div>
      </div>
    </v-form>
  </div>
</template>

<script>
import { categoryService } from '@/services';
import Compressor from 'compressorjs';

export default {
  data () {
    return {
      id: null,
      name: '',
      description: '',
      parentCategory: null,
      popupNotes: '',
      ageCheck: false,
      isExcludedFromDiscount: false,
      eposOnly: false,
      hidden: false,
      position: 0,

      imageBase64: null,
      image: null,

      categories: [],

      submitted: false,
      isSaving: false,

      valid: true,
      openPanels: [0, 1, 2],
      nameRules: [ v => !!v || 'Name is required', ],
    }
  },
  computed: {
    formTitle() {
      return this.id == null ? 'Add category' : 'Edit category';
    }
  },
  async created () {
    await this.loadCategories();

    var id = this.$route.params.id;
    if (id != null) {
      var dbCategory = await categoryService.getById(id);
      this.id = dbCategory.id;
      this.name = dbCategory.name;
      this.description = dbCategory.description;
      if (dbCategory.parentCategory != null)
        this.parentCategory = this.categories.find(c => c.id == dbCategory.parentCategory.id);
      this.popupNotes = dbCategory.popupNotes;
      this.ageCheck = dbCategory.ageCheck;
      this.eposOnly = dbCategory.eposOnly;
      this.hidden = dbCategory.hidden;
      this.isExcludedFromDiscount = dbCategory.isExcludedFromDiscount;
      this.position = dbCategory.position;

      this.imageBase64 = dbCategory.imageBase64;

      var idx = this.categories.indexOf(this.categories.find(c => c.id == this.id));
      if (idx > -1) {
        this.categories.splice(idx, 1);
      }
    }
  },
  methods: {
    goBack: function() {
      this.$router.go(-1);
    },
    loadCategories: async function() {
      this.categories = await categoryService.get();
      this.categories = this.categories.sort((a, b) => a.name.localeCompare(b.name));
      this.categories.unshift({
        id: null,
        name: 'None'
      });
      this.parentCategory = this.categories[0];
    },
    async handleSubmit () {
      if (!this.$refs.form.validate()) {
        this.openPanels = [0, 1, 2];
        return;
      }

      this.submitted = true;
      this.isSaving = true;

      var dto = {
        id: this.id,
        name: this.name,
        description: this.description,
        parentCategoryId: this.parentCategory.id,
        popupNotes: this.popupNotes,
        ageCheck: this.ageCheck,
        eposOnly: this.eposOnly,
        hidden: this.hidden,
        isExcludedFromDiscount: this.isExcludedFromDiscount,
        position: this.position,
        imageBase64: this.imageBase64,
      }

      try {
        var response = null;
        if (dto.id != null)
          response = await categoryService.update(dto);
        else
          response = await categoryService.add(dto);

        if (response.status != 200) {
          alert('Error saving');
          return;
        }

        this.$router.go(-1);
      } finally {
        this.isSaving = false;
      }
    },
    upload: function(file) {
      if (!file) {
        return;
      }

      new Compressor(file, {
        quality: 0.6,
        maxWidth: 512,

        success: (result) => {
          let reader = new FileReader();
          reader.onload = () => {
            this.imageBase64 = reader.result;
          }
          reader.readAsDataURL(result);
        },
        error(err) {
          console.log(err.message);
        },
      });
    },
    removeImage() {
      this.image = null;
      this.imageBase64 = null;
    },
  }
}
</script>

<style lang="scss" scoped>
.category-editor {
  padding-bottom: 8px;
  width: 100%;
}

.category-editor__header {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.category-editor__back {
  background: #ffffff;
  border: 1px solid #dfe7ef;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.06);
}

.category-editor__title {
  min-width: 0;

  span {
    color: #64748b;
    display: block;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.2;
    text-transform: uppercase;
  }

  h2 {
    color: #111827;
    font-size: 24px;
    font-weight: 800;
    line-height: 1.15;
    margin: 2px 0 0;
  }
}

.category-editor__form {
  display: grid;
  gap: 12px;
  width: 100%;
}

.category-sections {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
}

.category-section {
  border: 1px solid #dfe7ef;
  border-radius: 8px !important;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  width: 100%;

  &::before {
    display: none;
  }
}

.category-section__header {
  display: grid;
  gap: 2px;

  span {
    color: #0f766e;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  strong {
    color: #111827;
    font-size: 16px;
  }
}

.form-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

.option-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr;
}

.option-card {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #dfe7ef;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  min-height: 58px;
  padding: 0 12px;

  ::v-deep .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;
    width: 100%;
  }

  ::v-deep .v-input__slot {
    align-items: center;
    margin-bottom: 0;
  }
}

.image-uploader {
  display: grid;
  gap: 14px;
}

.image-preview {
  align-items: start;
  display: grid;
  gap: 10px;

  img {
    border: 1px solid #dfe7ef;
    border-radius: 8px;
    max-width: 220px;
    width: 100%;
  }
}

.category-editor__save-spacer {
  height: 78px;
}

.category-save-bar {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #dfe7ef;
  border-radius: 8px;
  bottom: 10px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
  display: grid;
  gap: 8px;
  padding: 10px;
  position: sticky;
  z-index: 5;
}

.category-save-bar__actions {
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr;
}

@media (min-width: 720px) {
  .category-editor__title h2 {
    font-size: 28px;
  }

  .form-grid--two {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .option-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .category-save-bar__actions {
    align-items: center;
    grid-template-columns: auto minmax(180px, auto);
    justify-content: end;
  }
}
</style>
